import { create } from 'zustand';
import { workItemApi } from '../services/api';
import type { WorkItem } from '../types';

interface WorkItemState {
  items: WorkItem[];
  loading: boolean;      // initial load — no items on screen yet
  refreshing: boolean;   // re-fetch while items are already shown (stale-while-revalidate)
  error: string | null;

  fetchByProject: (projectId: string, view?: string) => Promise<void>;
  fetchByProjects: (projectIds: string[], view?: string) => Promise<void>;
  createItem: (data: Partial<WorkItem>) => Promise<WorkItem>;
  updateItem: (id: string, data: Partial<WorkItem>) => Promise<WorkItem>;
  updateStatus: (id: string, status: string) => Promise<WorkItem>;
  reorderItem: (id: string, order: number, status?: string) => Promise<WorkItem>;
  deleteItem: (id: string) => Promise<void>;
  getChildren: (parentId: string) => WorkItem[];
  getByStatus: (status: string) => WorkItem[];
  getHierarchy: () => WorkItem[];
}

// ── Board-load instrumentation (Track 0) ────────────────────────────
// Lightweight timing around the board fetch so we can measure perceived
// speed (time-to-first-item, time-to-complete) and per-project cost. Uses
// the Performance API when available and always logs a console summary.
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const perfMark = (name: string) => {
  try {
    performance.mark?.(name);
  } catch {
    /* Performance API unavailable — ignore */
  }
};

const perfMeasure = (name: string, start: string, end: string) => {
  try {
    performance.measure?.(name, start, end);
  } catch {
    /* ignore */
  }
};

/** Fetch one project's work items, logging its duration and item count.
 *  Pass view='board' for the light card projection (smaller payload). */
const fetchProjectTimed = async (projectId: string, view?: string) => {
  const start = nowMs();
  const filters: Record<string, string> = { projectId };
  if (view) filters.view = view;
  const items = await workItemApi.list(filters);
  const ms = Math.round(nowMs() - start);
  console.debug(`[board] project ${projectId}: ${items.length} items in ${ms}ms`);
  return items;
};

// Monotonic token so a newer board fetch supersedes any in-flight older one.
// Without this, a slow response for a previously-selected scope could land
// after the user has switched filters and overwrite the board with stale items.
let fetchSeq = 0;

/** Above this many projects in one fetch, something has probably lost its
 *  scope. Portfolio-style views legitimately exceed it; boards do not. */
const BROAD_FETCH_WARN_THRESHOLD = 10;

/** Append incoming items to acc, skipping any _id already seen. */
const mergeUnique = (acc: WorkItem[], seen: Set<string>, incoming: WorkItem[]) => {
  for (const it of incoming) {
    if (!seen.has(it._id)) {
      seen.add(it._id);
      acc.push(it);
    }
  }
};

export const useWorkItemStore = create<WorkItemState>((set, get) => ({
  items: [],
  loading: false,
  refreshing: false,
  error: null,

  fetchByProject: async (projectId, view) => {
    const token = ++fetchSeq;
    // Stale-while-revalidate: keep current items on screen during a refetch
    // (only show the blocking "loading" state when there's nothing to show).
    const hadItems = get().items.length > 0;
    set({ loading: !hadItems, refreshing: hadItems, error: null });
    perfMark('board-fetch-start');
    const t0 = nowMs();
    try {
      const items = await fetchProjectTimed(projectId, view);
      if (token !== fetchSeq) return; // superseded by a newer fetch
      perfMark('board-first-item');
      perfMark('board-fetch-complete');
      perfMeasure('board-fetch', 'board-fetch-start', 'board-fetch-complete');
      console.info(
        `[board] loaded ${items.length} items across 1 project in ${Math.round(nowMs() - t0)}ms`
      );
      set({ items, loading: false, refreshing: false });
    } catch (err: any) {
      if (token !== fetchSeq) return;
      set({ error: err.message, loading: false, refreshing: false });
    }
  },

  fetchByProjects: async (projectIds, view) => {
    // EP-16 FE-1 guard. BG-194 was an unscoped fetch across 62 projects and
    // 18,891 items; the scope is now decided by the caller, so an empty list
    // means "nothing chosen yet", not "load everything".
    if (!projectIds || projectIds.length === 0) {
      set({ items: [], loading: false, refreshing: false, error: null });
      return;
    }
    // Loud rather than silent: a broad fetch is legitimate for the portfolio
    // views, but if one appears on a board it is a regression worth noticing.
    if (projectIds.length > BROAD_FETCH_WARN_THRESHOLD) {
      console.warn(
        `[board] fetching across ${projectIds.length} projects — expected a ` +
        'narrower scope. See EP-16 / BG-194.'
      );
    }

    if (projectIds.length === 1) {
      return get().fetchByProject(projectIds[0], view);
    }
    const token = ++fetchSeq;
    const hadItems = get().items.length > 0;
    set({ loading: !hadItems, refreshing: hadItems, error: null });
    perfMark('board-fetch-start');
    const t0 = nowMs();
    const acc: WorkItem[] = [];
    const seen = new Set<string>();
    let firstApplied = false;
    try {
      // Fire all project requests together and render each as it resolves —
      // no Promise.all barrier, so one slow project no longer blocks the rest.
      await Promise.all(
        projectIds.map((pid) =>
          fetchProjectTimed(pid, view).then((items) => {
            if (token !== fetchSeq) return; // stale selection — drop this result
            mergeUnique(acc, seen, items);
            if (!firstApplied) {
              firstApplied = true;
              perfMark('board-first-item');
              console.debug(`[board] first project resolved at ${Math.round(nowMs() - t0)}ms`);
            }
            // Progressive render: on the first result the stale items are
            // replaced; subsequent results grow the board column-by-column.
            set({ items: [...acc] });
          })
        )
      );
      if (token !== fetchSeq) return; // superseded before completion
      perfMark('board-fetch-complete');
      perfMeasure('board-fetch', 'board-fetch-start', 'board-fetch-complete');
      console.info(
        `[board] loaded ${acc.length} items across ${projectIds.length} projects in ${Math.round(nowMs() - t0)}ms`
      );
      set({ items: acc, loading: false, refreshing: false });
    } catch (err: any) {
      if (token !== fetchSeq) return;
      set({ error: err.message, loading: false, refreshing: false });
    }
  },

  createItem: async (data) => {
    const item = await workItemApi.create(data);
    set((s) => ({ items: [...s.items, item] }));
    return item;
  },

  updateItem: async (id, data) => {
    const updated = await workItemApi.update(id, data);
    if (updated?.awaitingApproval) {
      // A status change filed for approval: nothing in this update was applied.
      set((s) => ({
        items: s.items.map((i) => (i._id === id ? { ...i, pendingApproval: updated.awaitingApproval } : i)),
      }));
      throw Object.assign(new Error(updated.message || 'This move needs approval.'),
        { awaitingApproval: updated.awaitingApproval });
    }
    set((s) => ({
      items: s.items.map((i) => (i._id === id ? { ...i, ...updated } : i)),
    }));
    return updated;
  },

  updateStatus: async (id, status) => {
    const updated = await workItemApi.updateStatus(id, status);
    if (updated?.awaitingApproval) {
      // 202: filed for approval (EP-26 FE-7). The item has NOT moved — merging this
      // body would set its status to "awaiting_approval". Record the pending request
      // and throw, so the caller shows the message and the card stays where it is.
      set((s) => ({
        items: s.items.map((i) => (i._id === id ? { ...i, pendingApproval: updated.awaitingApproval } : i)),
      }));
      throw Object.assign(new Error(updated.message || 'This move needs approval.'),
        { awaitingApproval: updated.awaitingApproval });
    }
    set((s) => ({
      items: s.items.map((i) => (i._id === id ? { ...i, ...updated } : i)),
    }));
    return updated;
  },

  reorderItem: async (id, order, status) => {
    const updated = await workItemApi.reorder(id, order, status);
    set((s) => ({
      items: s.items.map((i) => (i._id === id ? { ...i, ...updated } : i)),
    }));
    return updated;
  },

  deleteItem: async (id) => {
    await workItemApi.delete(id);
    set((s) => ({ items: s.items.filter((i) => i._id !== id) }));
  },

  getChildren: (parentId) => {
    return get().items
      .filter((i) => i.parentId === parentId)
      .sort((a, b) => a.order - b.order);
  },

  getByStatus: (status) => {
    return get().items
      .filter((i) => i.status === status)
      .sort((a, b) => a.order - b.order);
  },

  getHierarchy: () => {
    const items = get().items;
    // Return top-level items (no parent), sorted by order
    return items
      .filter((i) => !i.parentId)
      .sort((a, b) => a.order - b.order);
  },
}));
